import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Popconfirm, message, Space, Card, Row, Col, Statistic, Collapse, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FilterOutlined, ClearOutlined, EyeOutlined } from '@ant-design/icons';
import { islemlerAPI, carilerAPI, kasalarAPI, giderTipleriAPI, eslesmelerAPI } from '../services/api';
import { formatPara, paraInputProps } from '../utils/format';
import { negatifKayitOnayi } from '../utils/onay';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';
import dayjs from 'dayjs';

const ISLEM_TIPI_ID = 9; // Odeme

function Odemeler() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [kasalar, setKasalar] = useState([]);
  const [giderTipleri, setGiderTipleri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreCariIds, setFiltreCariIds] = useState([]);
  const [filtreKasaIds, setFiltreKasaIds] = useState([]);
  const [filtreGiderTipiIds, setFiltreGiderTipiIds] = useState([]);
  const [filtreTutarOp, setFiltreTutarOp] = useState(null);
  const [filtreTutarDeger, setFiltreTutarDeger] = useState(null);
  const [filtreTarihBaslangic, setFiltreTarihBaslangic] = useState(null);
  const [filtreTarihBitis, setFiltreTarihBitis] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [eslesmeModalOpen, setEslesmeModalOpen] = useState(false);
  const [eslesmeData, setEslesmeData] = useState({ kayit: null, eslesmeler: [] });

  const handleEslesmeAc = async (record) => {
    setEslesmeData({ kayit: record, eslesmeler: [] });
    setEslesmeModalOpen(true);
    try {
      const res = await eslesmelerAPI.getAll({ kaynak_tip: 'odeme', kaynak_id: record.id, durum: 'aktif' });
      setEslesmeData({ kayit: record, eslesmeler: res.data || [] });
    } catch (err) {
      message.error('Eşleşmeler yüklenemedi');
    }
  };
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [islemlerRes, carilerRes, kasalarRes, giderTipleriRes] = await Promise.all([
        islemlerAPI.getAll({ islem_tipi_id: ISLEM_TIPI_ID }),
        carilerAPI.getAll(),
        kasalarAPI.getAll(),
        giderTipleriAPI.getAll(),
      ]);
      setData(islemlerRes.data);
      setCariler(carilerRes.data.filter(c => c.aktif !== 0));
      setKasalar(kasalarRes.data.filter(k => k.aktif));
      setGiderTipleri(giderTipleriRes.data);
    } catch (err) {
      message.error('Ödemeler yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const duzenleId = searchParams.get('duzenle');
    if (duzenleId && data.length > 0) {
      const record = data.find(d => String(d.id) === duzenleId);
      if (record) {
        setEditingRecord(record);
        setModalOpen(true);
        navigate('/odemeler', { replace: true });
      }
    }
  }, [data, searchParams, navigate]);

  useEffect(() => {
    if (!modalOpen) return;
    if (editingRecord) {
      form.setFieldsValue({
        cari_id: editingRecord.cari_id,
        kasa_id: editingRecord.kasa_id,
        tutar: editingRecord.tutar,
        gider_tipi_id: editingRecord.gider_tipi_id,
        aciklama: editingRecord.aciklama,
        tarih: editingRecord.tarih ? dayjs(editingRecord.tarih) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ tarih: dayjs() });
    }
  }, [modalOpen, editingRecord, form]);

  const handleSubmit = async (values) => {
    if (Number(values.tutar) < 0 && !(await negatifKayitOnayi('ödeme'))) return;
    const payload = {
      ...values,
      islem_tipi_id: ISLEM_TIPI_ID,
      tarih: values.tarih?.format('YYYY-MM-DD'),
    };
    try {
      if (editingRecord) {
        await islemlerAPI.update(editingRecord.id, payload);
        message.success('Ödeme güncellendi');
      } else {
        await islemlerAPI.create(payload);
        message.success('Ödeme eklendi');
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Güncellenemedi: ' : 'Eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await islemlerAPI.remove(id);
      message.success('Ödeme silindi');
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(d => {
      if (aramaMetni) {
        const q = aramaMetni.toLowerCase();
        if (!((d.cari || '').toLowerCase().includes(q) || (d.aciklama || '').toLowerCase().includes(q))) return false;
      }
      if (!secimeUyuyor(filtreCariIds, d.cari_id)) return false;
      if (!secimeUyuyor(filtreKasaIds, d.kasa_id)) return false;
      if (!secimeUyuyor(filtreGiderTipiIds, d.gider_tipi_id)) return false;
      if (filtreTutarOp && filtreTutarDeger != null) {
        const tutar = Number(d.tutar || 0), deger = Number(filtreTutarDeger);
        if (filtreTutarOp === 'gt' && !(tutar > deger)) return false;
        if (filtreTutarOp === 'gte' && !(tutar >= deger)) return false;
        if (filtreTutarOp === 'lt' && !(tutar < deger)) return false;
        if (filtreTutarOp === 'lte' && !(tutar <= deger)) return false;
        if (filtreTutarOp === 'eq' && !(tutar === deger)) return false;
      }
      if (filtreTarihBaslangic && dayjs(d.tarih).isBefore(filtreTarihBaslangic, 'day')) return false;
      if (filtreTarihBitis && dayjs(d.tarih).isAfter(filtreTarihBitis, 'day')) return false;
      return true;
    });
  }, [data, aramaMetni, filtreCariIds, filtreKasaIds, filtreGiderTipiIds, filtreTutarOp, filtreTutarDeger, filtreTarihBaslangic, filtreTarihBitis]);

  const toplamlar = useMemo(() => {
    const t = { adet: filteredData.length, tutar: 0 };
    filteredData.forEach(d => { t.tutar += Number(d.tutar || 0); });
    return t;
  }, [filteredData]);

  const filtreAktifMi = filtreCariIds.length > 0 || filtreKasaIds.length > 0 || filtreGiderTipiIds.length > 0 || filtreTutarOp || filtreTarihBaslangic || filtreTarihBitis;
  const filtreTemizle = () => { setFiltreCariIds([]); setFiltreKasaIds([]); setFiltreGiderTipiIds([]); setFiltreTutarOp(null); setFiltreTutarDeger(null); setFiltreTarihBaslangic(null); setFiltreTarihBitis(null); };

  const cariFilterOptions = useMemo(() => {
    const map = new Map();
    data.forEach(d => { if (d.cari_id && d.cari) map.set(d.cari_id, d.cari); });
    const list = Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    return bosSecenekliOptions(list, data, 'cari_id');
  }, [data]);

  const kasaFilterOptions = useMemo(() => {
    const map = new Map();
    data.forEach(d => { if (d.kasa_id && d.kasa_ad) map.set(d.kasa_id, d.kasa_ad); });
    const list = Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    return bosSecenekliOptions(list, data, 'kasa_id');
  }, [data]);

  const giderTipiFilterOptions = useMemo(
    () => bosSecenekliOptions(giderTipleri.map(g => ({ value: g.id, label: g.ad })), data, 'gider_tipi_id'),
    [giderTipleri, data]
  );

  const columns = [
    { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: isMobile ? 65 : 110, render: (v) => v ? dayjs(v).format(isMobile ? 'DD.MM.YY' : 'DD.MM.YYYY') : '-', sorter: (a, b) => { const d = new Date(a.tarih || 0) - new Date(b.tarih || 0); return d !== 0 ? d : (a.id || 0) - (b.id || 0); }, defaultSortOrder: 'descend' },
    { title: 'Cari', dataIndex: 'cari', key: 'cari', sorter: (a, b) => (a.cari || '').localeCompare(b.cari || '', 'tr'), render: (v, r) => r.cari_id ? <a onClick={(e) => { e.stopPropagation(); navigate(`/cariler?detay=${r.cari_id}`); }}>{v}</a> : v },
    { title: 'Kasa', dataIndex: 'kasa_ad', key: 'kasa_ad', responsive: ['sm'], sorter: (a, b) => (a.kasa_ad || '').localeCompare(b.kasa_ad || '', 'tr') },
    { title: 'Tutar', dataIndex: 'tutar', key: 'tutar', width: isMobile ? 90 : 120, align: 'right', render: (v) => v != null ? formatPara(v) : '-', sorter: (a, b) => (a.tutar || 0) - (b.tutar || 0) },
    { title: 'Gider Tipi', dataIndex: 'gider_tipi', key: 'gider_tipi', width: 130, responsive: ['md'], sorter: (a, b) => (a.gider_tipi || '').localeCompare(b.gider_tipi || '', 'tr') },
    { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama', responsive: ['lg'] },
    {
      title: 'İşlem', key: 'islem', width: isMobile ? 110 : 200,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleEslesmeAc(record)} title="Eşleşmeleri Gör" />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{!isMobile && 'Düzenle'}</Button>
          <Popconfirm title="Bu ödemeyi silmek istediğinize emin misiniz? Eşleşmeleri iptal edilecek." onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Ödemeler</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
          <Button icon={<FilterOutlined />} onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])} type={filtreAktifMi ? 'primary' : 'default'} ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}>
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} title="Yeni Ödeme">
            {!isMobile && 'Yeni Ödeme'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Cari</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Cari seçin..." options={cariFilterOptions} value={filtreCariIds} onChange={setFiltreCariIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Kasa</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Kasa seçin..." options={kasaFilterOptions} value={filtreKasaIds} onChange={setFiltreKasaIds} maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Gider Tipi</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Gider tipi seçin..." options={giderTipiFilterOptions} value={filtreGiderTipiIds} onChange={setFiltreGiderTipiIds} maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tutar Filtresi</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreTutarOp} onChange={setFiltreTutarOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Tutar" value={filtreTutarDeger} onChange={setFiltreTutarDeger} min={0} />
              </Space.Compact>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Başlangıç</label>
              <DatePicker style={{ width: '100%' }} placeholder="Başlangıç" value={filtreTarihBaslangic} onChange={setFiltreTarihBaslangic} format="DD.MM.YYYY" />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Bitiş</label>
              <DatePicker style={{ width: '100%' }} placeholder="Bitiş" value={filtreTarihBitis} onChange={setFiltreTarihBitis} format="DD.MM.YYYY" />
            </Col>
          </Row>
        </Card>
      )}]} />

      {(
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={6}><Statistic title="Kayıt Sayısı" value={toplamlar.adet} /></Col>
            <Col xs={12} sm={8} md={6}><Statistic title="Toplam Tutar" value={toplamlar.tutar} precision={2} suffix="₺" valueStyle={{ color: '#cf1322' }} /></Col>
          </Row>
        </Card>
      )}

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      <Modal title={editingRecord ? 'Ödeme Düzenle' : 'Yeni Ödeme'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }} onOk={() => form.submit()}
        width={isMobile ? '95vw' : 480}
        style={isMobile ? { top: 10, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: 12 } } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="tarih" label="Tarih" rules={[{ required: true }]} initialValue={dayjs()}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cari_id" label="Cari">
            <Select allowClear showSearch optionFilterProp="label" placeholder="Cari seçin (opsiyonel)" options={cariler.map(c => ({ value: c.id, label: c.ad }))} />
          </Form.Item>
          <Form.Item name="kasa_id" label="Kasa" rules={[{ required: true, message: 'Kasa seçimi zorunludur' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Kasa seçin" options={kasalar.map(k => ({ value: k.id, label: k.ad }))} />
          </Form.Item>
          <Form.Item name="tutar" label="Tutar (₺)" rules={[{ required: true }]}>
            <InputNumber {...paraInputProps} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="gider_tipi_id" label="Gider Tipi">
            <Select allowClear showSearch optionFilterProp="label" placeholder="Gider tipi seçin" options={giderTipleri.map(g => ({ value: g.id, label: g.ad }))} />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={eslesmeData.kayit ? `Ödeme #${eslesmeData.kayit.id} — Eşleşme Detayı` : 'Eşleşme'}
        open={eslesmeModalOpen}
        onCancel={() => setEslesmeModalOpen(false)}
        footer={<Button onClick={() => setEslesmeModalOpen(false)}>Kapat</Button>}
        width={isMobile ? '95vw' : 700}
      >
        {eslesmeData.kayit && (
          <Card size="small" style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col span={8}><Statistic title="Tutar" value={eslesmeData.kayit.tutar} precision={2} suffix="₺" /></Col>
              <Col span={8}><Statistic title="Eşleşen" value={eslesmeData.eslesmeler.reduce((t, e) => t + Number(e.tutar || 0), 0)} precision={2} suffix="₺" /></Col>
              <Col span={8}><Statistic title="Avans Kalan" value={Number(eslesmeData.kayit.tutar || 0) - eslesmeData.eslesmeler.reduce((t, e) => t + Number(e.tutar || 0), 0)} precision={2} suffix="₺" valueStyle={{ color: '#fa8c16' }} /></Col>
            </Row>
          </Card>
        )}
        <Table
          size="small"
          dataSource={eslesmeData.eslesmeler}
          rowKey="id"
          pagination={false}
          columns={[
            { title: 'Eşleşme Tarihi', dataIndex: 'eslesme_tarihi', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-' },
            { title: 'Hedef', dataIndex: 'hedef_tip' },
            { title: 'Vade ID', dataIndex: 'hedef_id', render: (v, r) => r.hedef_tip === 'alis_vadesi' ? <a onClick={() => navigate(`/alislar?duzenle=${r.hedef_id}`)}>{v}</a> : v },
            { title: 'Tutar', dataIndex: 'tutar', align: 'right', render: (v) => <strong>{formatPara(v)}</strong> },
            { title: 'Açıklama', dataIndex: 'aciklama' },
          ]}
          locale={{ emptyText: 'Eşleşme yok (avans olarak duruyor)' }}
        />
      </Modal>
    </div>
  );
}

export default Odemeler;
